import React, { useState, useEffect } from "react";
import { database } from '../../Firebase';
import { ref, onValue } from 'firebase/database';
import { useNavigate } from 'react-router-dom';

const TaxReceipt = () => {
  const navigate = useNavigate();
  const [advanceSearch, setAdvanceSearch] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taxPayments, setTaxPayments] = useState({});

  // Fetch मिळकत क्र data from Firebase
  useEffect(() => {
    const milakhatRecordsRef = ref(database, 'Grampanchayat/Gramniddhi/NewMilakhatRecords');
    
    const unsubscribe = onValue(milakhatRecordsRef, (snapshot) => {
      const data = snapshot.val();
      const milakhatRecords = [];
      
      if (data) {
        Object.keys(data).forEach(propertyNo => {
          const record = data[propertyNo];
          milakhatRecords.push({
            id: propertyNo,
            owner: record.mainOwner || '',
            pending: record.taxData?.total?.total || 0
          });
        });
      }
      
      setRecords(milakhatRecords);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching milakhat data:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Fetch tax payment data to check who has paid
  useEffect(() => {
    const karbharnaRef = ref(database, 'Grampanchayat/Gramniddhi/karbharna');
    
    const unsubscribe = onValue(karbharnaRef, (snapshot) => {
      const data = snapshot.val();
      const payments = {};
      
      if (data) {
        Object.keys(data).forEach(propertyNo => {
          const propertyPayments = data[propertyNo];
          if (propertyPayments) {
            // Get the latest payment for this property
            const receiptNumbers = Object.keys(propertyPayments);
            if (receiptNumbers.length > 0) {
              // Sort by timestamp to get the latest payment
              const latestReceipt = receiptNumbers.reduce((latest, current) => {
                const latestTime = propertyPayments[latest]?.timestamp || '';
                const currentTime = propertyPayments[current]?.timestamp || '';
                return currentTime > latestTime ? current : latest;
              });
              
              payments[propertyNo] = {
                hasPaid: true,
                paymentData: propertyPayments[latestReceipt],
                remainingAmount: propertyPayments[latestReceipt]?.totals?.grandTotal || 0
              };
            }
          }
        });
      }
      
      setTaxPayments(payments);
    }, (error) => {
      console.error("Error fetching tax payment data:", error);
    });

    return () => unsubscribe();
  }, []);

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="bg-white border rounded shadow">

        {/* Header */}
        <div className="bg-[#267c77] text-white px-4 py-3 text-lg font-semibold">
          कर भरणा
        </div>

        {/* Advance Search */}
        <div className="bg-[#267c77] px-4 py-3 flex items-center gap-2">
          <input
            type="checkbox"
            checked={advanceSearch}
            onChange={() => setAdvanceSearch(!advanceSearch)}
            className="w-4 h-4"
          />
          <span className="font-medium text-white-900">
            अधिक शोध (Advance Search)
          </span>
        </div>

        {/* Search Input */}
        <div className="flex p-4 gap-2">
          <input
            type="text"
            placeholder="मिळकत क्रमांक / मिळकत धारकाचे नाव"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="flex-1 border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#267c77]"
          />
          <button className="bg-green-600 text-white px-6 rounded hover:bg-green-700">
            शोधणे
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[#267c77] text-white">
                <th className="border px-4 py-2">मिळकत नं.</th>
                <th className="border px-4 py-2">मालकाचे नाव</th>
                <th className="border px-4 py-2">कर बाकी</th>
                <th className="border px-4 py-2">पर्याय</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="4"
                    className="text-center px-4 py-3 text-gray-700"
                  >
                    Loading...
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td
                    colSpan="4"
                    className="text-center px-4 py-3 text-gray-700"
                  >
                    No records found
                  </td>
                </tr>
              ) : (
                records
                  .filter(record => 
                    searchText === '' || 
                    record.id.toLowerCase().includes(searchText.toLowerCase()) ||
                    record.owner.toLowerCase().includes(searchText.toLowerCase())
                  )
                  .map((item) => {
                    const hasPaid = taxPayments[item.id]?.hasPaid || false;
                    const paymentData = taxPayments[item.id]?.paymentData;
                    const remainingAmount = hasPaid ? 0 : item.pending;
                    
                    return (
                    <tr key={item.id} className="text-center">
                      <td className="border px-4 py-2">{item.id}</td>
                      <td className="border px-4 py-2">{item.owner}</td>
                      <td className="border px-4 py-2">{remainingAmount.toFixed(2)}</td>
                      <td className="border px-4 py-2">
                        {hasPaid ? (
                          <button 
                            onClick={() => navigate('/karpavthi', { state: { paymentData: paymentData, userData: item }})}
                            className="bg-[#267c77] text-white px-4 py-1 rounded hover:bg-[#267c77]"
                          >
                            कर पावती
                          </button>
                        ) : (
                          <button 
                            onClick={() => navigate('/taxcollectionform', { state: { userData: item }})}
                            className="bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700"
                          >
                            कर भरणा
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-gradient-to-r from-[#267c77] to-[#267c77] text-white flex items-center justify-center gap-3 py-3">
          <button className="opacity-50">⏮</button>
          <button className="opacity-50">◀</button>

          <span className="bg-blue-600 px-3 py-1 rounded border border-white">
            1
          </span>

          <button className="opacity-50">▶</button>
          <button className="opacity-50">⏭</button>

          <select className="ml-4 text-black px-2 py-1 rounded">
            <option>5</option>
            <option>10</option>
            <option>20</option>
          </select>
        </div>

      </div>
    </div>
  );
};

export default TaxReceipt;
